import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import ExportToolbar from "../components/ExportToolbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Library, Loader2, Search, Filter, GitCompare, Sparkles, BookOpen,
  ChevronDown, ChevronRight, CheckCircle, Globe, FileText, Lightbulb
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ExpandedClauseLibraryView = () => {
  const [activeTab, setActiveTab] = useState("frameworks");
  const [frameworks, setFrameworks] = useState({});
  const [contractTypes, setContractTypes] = useState({});
  const [clauses, setClauses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterFramework, setFilterFramework] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [expandedFramework, setExpandedFramework] = useState(null);
  const [compareResult, setCompareResult] = useState(null);
  const [compareFramework1, setCompareFramework1] = useState("");
  const [compareFramework2, setCompareFramework2] = useState("");
  const [generatedClauses, setGeneratedClauses] = useState(null);
  const [genForm, setGenForm] = useState({ framework: "FIDIC", contract_type: "EPC", sector: "Infrastructure", requirements: "" });

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [fwRes, ctRes, clRes] = await Promise.all([
        axios.get(`${API}/clause-library-v2/frameworks`),
        axios.get(`${API}/clause-library-v2/contract-types`),
        axios.get(`${API}/clause-library-v2/clauses`)
      ]);
      setFrameworks(fwRes.data.frameworks || {});
      setContractTypes(ctRes.data.contract_types || {});
      setClauses(clRes.data.clauses || []);
    } catch (e) { console.log("Failed to fetch library data"); }
  };

  const compareFrameworks = async () => {
    if (!compareFramework1 || !compareFramework2) { toast.error("Select both frameworks"); return; }
    setLoading(true);
    try {
      const res = await axios.get(`${API}/clause-library-v2/framework-comparison?framework1=${compareFramework1}&framework2=${compareFramework2}`);
      setCompareResult(res.data);
      toast.success("Comparison complete");
    } catch (e) { toast.error("Comparison failed"); }
    finally { setLoading(false); }
  };

  const generateClauses = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/clause-library-v2/ai-generate-clauses?framework=${genForm.framework}&contract_type=${genForm.contract_type}&sector=${genForm.sector}&specific_requirements=${encodeURIComponent(genForm.requirements)}`);
      setGeneratedClauses(res.data.generated_clauses || []);
      toast.success("Clauses generated");
    } catch (e) { toast.error(e.response?.data?.detail || "Generation failed"); }
    finally { setLoading(false); }
  };

  const filteredClauses = clauses.filter(c => {
    if (filterFramework && !c.frameworks?.includes(filterFramework)) return false;
    if (filterCategory && c.category !== filterCategory) return false;
    return true;
  });

  const categories = [...new Set(clauses.map(c => c.category))];
  const frameworkNames = Object.keys(frameworks);

  return (
    <div className="space-y-6" data-testid="expanded-clause-library-view" data-export-region>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-black tracking-tight uppercase font-['Chivo']">Expanded Clause Library</h1>
          <p className="text-zinc-500 mt-1">{frameworkNames.length} frameworks, {Object.keys(contractTypes).length} contract types, {clauses.length}+ clauses</p>
        </div>
        <ExportToolbar title="Expanded Clause Library" pdfFilename="expanded-clause-library" />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-zinc-100 border-2 border-zinc-200">
          <TabsTrigger value="frameworks" data-testid="tab-frameworks"><Globe size={14} className="mr-1" /> Frameworks ({frameworkNames.length})</TabsTrigger>
          <TabsTrigger value="contracts" data-testid="tab-contracts"><FileText size={14} className="mr-1" /> Contract Types ({Object.keys(contractTypes).length})</TabsTrigger>
          <TabsTrigger value="clauses" data-testid="tab-clauses"><Library size={14} className="mr-1" /> Clauses ({clauses.length})</TabsTrigger>
          <TabsTrigger value="compare" data-testid="tab-compare"><GitCompare size={14} className="mr-1" /> Compare</TabsTrigger>
          <TabsTrigger value="generate" data-testid="tab-generate"><Sparkles size={14} className="mr-1" /> AI Generate</TabsTrigger>
        </TabsList>

        <TabsContent value="frameworks">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {frameworkNames.map(fw => {
              const info = frameworks[fw];
              const isExpanded = expandedFramework === fw;
              return (
                <div key={fw} className={`border-2 ${isExpanded ? 'border-[#EAB308]' : 'border-zinc-200'} bg-white p-4 cursor-pointer hover:border-[#EAB308] transition-colors`}
                  onClick={() => setExpandedFramework(isExpanded ? null : fw)} data-testid={`framework-${fw}`}>
                  <div className="flex items-center justify-between mb-2">
                    <Badge className="bg-[#EAB308] text-white border-none">{fw}</Badge>
                    {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </div>
                  <h3 className="text-sm font-bold mb-1">{info.name}</h3>
                  <p className="text-xs text-zinc-500">Origin: {info.origin}</p>
                  {isExpanded && (
                    <div className="mt-3 pt-3 border-t border-zinc-200 space-y-2">
                      <div>
                        <p className="text-xs font-bold uppercase text-zinc-400">Editions</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {info.editions?.map((ed, i) => <Badge key={i} variant="outline" className="text-xs">{ed}</Badge>)}
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase text-zinc-400">Use Cases</p>
                        <ul className="mt-1 space-y-1">
                          {info.use_cases?.map((uc, i) => (
                            <li key={i} className="text-xs flex items-center gap-1"><CheckCircle size={10} className="text-emerald-500" />{uc}</li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase text-zinc-400">Key Features</p>
                        <ul className="mt-1 space-y-1">
                          {info.key_features?.map((kf, i) => (
                            <li key={i} className="text-xs flex items-center gap-1"><Lightbulb size={10} className="text-amber-500" />{kf}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </TabsContent>

        <TabsContent value="contracts">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(contractTypes).map(([type, info]) => (
              <div key={type} className="border-2 border-zinc-200 bg-white p-4 hover:border-[#EAB308] transition-colors" data-testid={`contract-type-${type}`}>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-bold">{type.replace(/_/g, ' ')}</h3>
                  <Badge className={`border-none text-xs ${
                    info.risk_profile === 'employer_heavy' ? 'bg-blue-100 text-blue-700' :
                    info.risk_profile === 'contractor_heavy' ? 'bg-red-100 text-red-700' :
                    info.risk_profile === 'shared' ? 'bg-emerald-100 text-emerald-700' :
                    'bg-zinc-100 text-zinc-700'
                  }`}>{info.risk_profile?.replace(/_/g, ' ')}</Badge>
                </div>
                <p className="text-xs text-zinc-600 mb-2">{info.description}</p>
                <div className="flex flex-wrap gap-1">
                  {info.typical_frameworks?.map((fw, i) => <Badge key={i} variant="outline" className="text-xs">{fw}</Badge>)}
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="clauses">
          <div className="space-y-4">
            <div className="flex gap-3">
              <select value={filterFramework} onChange={e => setFilterFramework(e.target.value)} className="border-2 border-zinc-200 p-2 text-sm" data-testid="clause-filter-framework">
                <option value="">All Frameworks</option>
                {frameworkNames.map(fw => <option key={fw} value={fw}>{fw}</option>)}
              </select>
              <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="border-2 border-zinc-200 p-2 text-sm" data-testid="clause-filter-category">
                <option value="">All Categories</option>
                {categories.map(cat => <option key={cat} value={cat}>{cat.replace(/_/g, ' ')}</option>)}
              </select>
              <Badge variant="outline" className="self-center">{filteredClauses.length} clauses</Badge>
            </div>
            <ScrollArea className="h-[500px]">
              <div className="space-y-3">
                {filteredClauses.map((clause, i) => (
                  <div key={i} className="border-2 border-zinc-200 bg-white p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-bold">{clause.title}</h4>
                      <div className="flex items-center gap-2">
                        <Badge className={`border-none text-xs ${
                          clause.risk_level === 'critical' ? 'bg-red-100 text-red-700' :
                          clause.risk_level === 'high' ? 'bg-orange-100 text-orange-700' :
                          clause.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' :
                          'bg-green-100 text-green-700'
                        }`}>{clause.risk_level}</Badge>
                        <Badge variant="outline" className="text-xs">{clause.category?.replace(/_/g, ' ')}</Badge>
                      </div>
                    </div>
                    <p className="text-xs text-zinc-600">{clause.text}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {clause.frameworks?.map((fw, j) => <Badge key={j} variant="outline" className="text-xs bg-zinc-50">{fw}</Badge>)}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </TabsContent>

        <TabsContent value="compare">
          <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
            <h2 className="text-lg font-bold font-['Chivo']">Framework Comparison</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div>
                <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Framework 1</label>
                <select value={compareFramework1} onChange={e => setCompareFramework1(e.target.value)} className="w-full border-2 border-zinc-200 p-2" data-testid="compare-fw1">
                  <option value="">Select...</option>
                  {frameworkNames.map(fw => <option key={fw} value={fw}>{fw}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Framework 2</label>
                <select value={compareFramework2} onChange={e => setCompareFramework2(e.target.value)} className="w-full border-2 border-zinc-200 p-2" data-testid="compare-fw2">
                  <option value="">Select...</option>
                  {frameworkNames.map(fw => <option key={fw} value={fw}>{fw}</option>)}
                </select>
              </div>
              <Button onClick={compareFrameworks} disabled={loading} className="bg-black text-white" data-testid="compare-btn">
                {loading ? <Loader2 size={14} className="mr-1 animate-spin" /> : <GitCompare size={14} className="mr-1" />} Compare
              </Button>
            </div>

            {compareResult?.analysis && !compareResult.analysis.error && (
              <div className="space-y-4 mt-6 pt-6 border-t-2 border-zinc-200">
                {compareResult.analysis.comparison && Object.entries(compareResult.analysis.comparison).map(([aspect, data]) => (
                  <div key={aspect} className="p-4 border border-zinc-200">
                    <h4 className="text-xs font-bold uppercase text-zinc-500 mb-3">{aspect.replace(/_/g, ' ')}</h4>
                    <div className="grid grid-cols-3 gap-3 text-sm">
                      <div className={data.winner === compareFramework1 ? 'font-bold text-[#EAB308]' : ''}><Badge className="mb-1 bg-blue-100 text-blue-700 border-none">{compareFramework1}</Badge><p>{data.framework1}</p></div>
                      <div className="text-center self-center text-xs text-zinc-400">vs</div>
                      <div className={data.winner === compareFramework2 ? 'font-bold text-[#E11D48]' : ''}><Badge className="mb-1 bg-orange-100 text-orange-700 border-none">{compareFramework2}</Badge><p>{data.framework2}</p></div>
                    </div>
                  </div>
                ))}
                {compareResult.analysis.recommendation && (
                  <div className="p-4 bg-blue-50 border border-blue-200">
                    <p className="text-sm">{compareResult.analysis.recommendation}</p>
                  </div>
                )}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="generate">
          <div className="border-2 border-black brutal-shadow bg-white p-6 space-y-6">
            <h2 className="text-lg font-bold font-['Chivo']">AI Clause Generation</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Framework</label>
                <select value={genForm.framework} onChange={e => setGenForm(p => ({ ...p, framework: e.target.value }))} className="w-full border-2 border-zinc-200 p-2" data-testid="gen-framework">
                  {frameworkNames.map(fw => <option key={fw} value={fw}>{fw}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Contract Type</label>
                <select value={genForm.contract_type} onChange={e => setGenForm(p => ({ ...p, contract_type: e.target.value }))} className="w-full border-2 border-zinc-200 p-2" data-testid="gen-contract-type">
                  {Object.keys(contractTypes).map(ct => <option key={ct} value={ct}>{ct.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Sector</label>
                <select value={genForm.sector} onChange={e => setGenForm(p => ({ ...p, sector: e.target.value }))} className="w-full border-2 border-zinc-200 p-2" data-testid="gen-sector">
                  {["Infrastructure", "Energy", "Buildings", "Industrial", "Water", "Mining", "Healthcare", "Education"].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold uppercase text-zinc-500 block mb-1">Specific Requirements</label>
              <textarea value={genForm.requirements} onChange={e => setGenForm(p => ({ ...p, requirements: e.target.value }))} className="w-full border-2 border-zinc-200 p-3 text-sm h-20" placeholder="Describe specific clause requirements..." data-testid="gen-requirements" />
            </div>
            <Button onClick={generateClauses} disabled={loading} className="bg-[#E11D48] text-white px-8" data-testid="gen-clauses-btn">
              {loading ? <><Loader2 size={16} className="mr-2 animate-spin" /> Generating...</> : <><Sparkles size={16} className="mr-2" /> Generate Clauses</>}
            </Button>

            {generatedClauses && (
              <div className="space-y-3 mt-6 pt-6 border-t-2 border-zinc-200">
                <h3 className="text-xs font-bold uppercase text-zinc-500">Generated Clauses</h3>
                {generatedClauses.map((clause, i) => (
                  <div key={i} className="border-2 border-zinc-200 bg-zinc-50 p-4 space-y-2">
                    <div className="flex items-center justify-between">
                      <h4 className="text-sm font-bold">{clause.title}</h4>
                      <Badge className={`border-none text-xs ${clause.risk_level === 'critical' ? 'bg-red-100 text-red-700' : clause.risk_level === 'high' ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700'}`}>{clause.risk_level}</Badge>
                    </div>
                    <p className="text-xs text-zinc-700">{clause.text}</p>
                    {clause.rationale && <p className="text-xs text-zinc-500 italic">{clause.rationale}</p>}
                    {clause.negotiation_notes && (
                      <div className="flex items-start gap-2 mt-1">
                        <Lightbulb size={12} className="text-amber-500 mt-0.5" />
                        <p className="text-xs text-amber-700">{clause.negotiation_notes}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ExpandedClauseLibraryView;
